import { Fade, IconButton, Paper, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'

import { SkipReasonCount } from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import { skipReasonLabel } from '../../helpers/skipReasonLabels'
import { getTranslate } from '../../localization'
import SkipReasonListMobile from './SkipReasonListMobile'
import useStyles from './styles/index.styles'

interface Props {
  byReason: SkipReasonCount[]
  evaluationsExamined: number
  retentionDays: number
  /** Refetch, for the header-row refresh button §2 asks every table header to carry. */
  onRefresh: () => void
  /**
   * Computed ONCE at the screen root and passed down, rather than each table opening its
   * own useMediaQuery subscription for the same breakpoint. Matches PromotionsOverview,
   * which switches at the parent — three listeners for one boolean is three chances for the
   * screen to disagree with itself mid-resize.
   */
  isSmallScreen: boolean
}

/**
 * The headline answer: which reasons account for promotions not applying.
 *
 * ORDERED BY THE SERVER, not re-sorted here. It returns them by descending count, and a
 * client that re-sorted would assert its own idea of importance over the figures.
 *
 * THE HORIZON IS IN EVERY SENTENCE, not only in a note above the table. "This period" is a
 * deixis — it points at something the reader has to go and find, and at the default preset
 * the thing it points at is labelled "All retained", which reads as "everything". The number
 * has to be in the claim it qualifies, or the qualifier gets read separately and dropped.
 */
const SkipReasonSummary = ({
  byReason,
  evaluationsExamined,
  retentionDays,
  onRefresh,
  isSmallScreen
}: Props) => {
  const classes = useStyles()
  const days = formatCount(retentionDays)

  if (byReason.length === 0) {
    return (
      <Paper className={classes.section} data-cy="SkipReasonsEmpty">
        {/* The caption renders here too. Without it the empty state is an unlabelled box —
            the same naming asymmetry the caption was added to remove, returning in the one
            state where the page offers least other context. */}
        <Typography variant="h6" component="h2" className={classes.caption}>
          {getTranslate('Skips by reason')}
        </Typography>
        <Typography variant="body1" className={classes.caveat}>
          {evaluationsExamined === 0
            ? getTranslate(
                'No promotions were evaluated in the last {days} days.',
                {
                  days
                }
              )
            : getTranslate(
                'Every promotion evaluated in the last {days} days applied — nothing was skipped. Anything older is no longer kept.',
                { days }
              )}
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper className={classes.section}>
      {/*
        Explicit ARIA roles rather than a MUI Table. The portal's grid screens render tabular
        data in divs with no table semantics at all, so a screen reader reads a flat run of
        text with no way to tell which number belongs to which reason.
      */}
      {/*
        A VISIBLE caption, referenced, rather than an aria-label.

        aria-label gave screen-reader users a name sighted users never saw, so the page showed
        two unlabelled boxes of numbers distinguishable only by column count. Promoting it to
        a heading fixes the naming asymmetry and fills the h1 → h2 gap in one change.
      */}
      <Typography
        variant="h6"
        component="h2"
        id="skips-by-reason-caption"
        className={classes.caption}>
        {getTranslate('Skips by reason')}
      </Typography>
      {/* Layout only: the caption above and the note below stay single-sourced, so the
          breakpoints cannot drift on meaning — only on arrangement. */}
      {isSmallScreen ? (
        <SkipReasonListMobile byReason={byReason} onRefresh={onRefresh} />
      ) : (
        <div role="table" aria-labelledby="skips-by-reason-caption">
          <div
            role="row"
            className={`${classes.tableHead} ${classes.reasonColumns}`}>
            <Typography variant="body2" role="columnheader">
              {getTranslate('Reason')}
            </Typography>
            <Typography
              variant="body2"
              role="columnheader"
              className={classes.numeric}>
              {getTranslate('Skips')}
            </Typography>
            {/* Not a columnheader: it names no column. role="presentation" keeps it out of the
              table's column count, which a screen reader announces. */}
            <IconButton
              size="small"
              role="presentation"
              className={classes.refresh}
              onClick={onRefresh}
              data-cy="SkipReasonsRefresh"
              aria-label={getTranslate('Refresh')}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
          {byReason.map((row) => (
            <Fade in timeout={600} key={row.reason}>
              <div
                role="row"
                data-cy="SkipReasonRow"
                className={`${classes.tableRow} ${classes.reasonColumns}`}>
                <Typography
                  variant="body2"
                  component="span"
                  role="cell"
                  data-reason={row.reason}
                  className={classes.label}>
                  {getTranslate(skipReasonLabel(row.reason))}
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  role="cell"
                  className={classes.numeric}>
                  {formatCount(row.count)}
                </Typography>
                {/* The header's action column, empty here — the grid is one constant, so the
                rows carry the slot whether or not they use it. */}
                <span />
              </div>
            </Fade>
          ))}
        </div>
      )}

      {/*
        THE ONE MISREAD NO EMPTY STATE CATCHES. An operator hunting a specific hypothesis —
        "did my budget run out?" — scans this list, does not see that reason, and concludes it
        never happens. No zero renders, because the list carries only reasons that OCCURRED,
        so absence is the only form the misread can take and it is the form a note above the
        table does not reach. It is stated here, at the point of reading.
      */}
      <Typography
        variant="body2"
        className={classes.caveat}
        data-cy="SkipReasonAbsenceCaveat">
        {getTranslate(
          'Only reasons seen in the last {days} days appear here. A reason that is missing has not happened recently — not never.',
          { days }
        )}
      </Typography>
    </Paper>
  )
}

export default SkipReasonSummary
